import { Button, Flex } from '@aws-amplify/ui-react';
import { useCallback, useContext, useEffect, useState } from 'react';
import { TbArrowRight } from 'react-icons/tb';
import { Link, useNavigate } from 'react-router-dom';
import { HubspotProperty, HubspotPropertyPicker } from '../components/hubspot-companies/HubspotPropertyPicker';
import LoadingBars from '../custom-components/LoadingBars';
import NothingHere from '../custom-components/NothingHere';
import { BubbleOption, SelectableBubbles } from '../custom-components/SelectableBubbles';
import { GlobalContext } from '../global-context';
import { allPages, toastV4Error } from '../services/edulead-v4';
import { eduleadV4 } from '../services/edulead-v4-client';
import General from '../utils/GeneralUtils';
import './MatchRun.scss';

const STRATEGY_OPTIONS: BubbleOption[] = [
  {
    value: 'NCES_ID',
    label: 'NCES ID',
    description: 'Exact match on an NCES ID you already store in HubSpot',
  },
  {
    value: 'NAME_ADDRESS',
    label: 'Name + Address',
    description: 'Fuzzy match on company name, confirmed by address',
  },
];

export function MatchRunCreate() {
  const globalContext = useContext(GlobalContext);
  const navigate = useNavigate();
  General.setTitle(document, 'HubSpot Company Enrichment', globalContext);

  const [isCheckingConnection, setIsCheckingConnection] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [companyProperties, setCompanyProperties] = useState<HubspotProperty[]>([]);

  const [strategy, setStrategy] = useState<string[]>(['NCES_ID']);
  const [hsNcesIdField, setHsNcesIdField] = useState('');
  const [hsCompanyNameField, setHsCompanyNameField] = useState('name');
  const [hsStateField, setHsStateField] = useState('state');
  const [hsAddressField, setHsAddressField] = useState('');

  const [isCreating, setIsCreating] = useState(false);

  const loadConnection = useCallback(async () => {
    try {
      const hubspot = (await eduleadV4.integrations.get({ id: 'hubspot' })).integration;
      setIsConnected(!!hubspot?.connected);
      if (hubspot?.connected) {
        const properties = await allPages(
          eduleadV4.integrations.listProperties({ integration_id: 'hubspot', 'filter.object_type': 'INTEGRATION_OBJECT_TYPE_INSTITUTION', page_size: 500 })
        );
        setCompanyProperties(properties.map(p => ({ name: p.id ?? '', label: p.label ?? '', type: p.type ?? '' })));
      }
    } catch (error) {
      toastV4Error(error, 'Failed to check the HubSpot connection');
    } finally {
      setIsCheckingConnection(false);
    }
  }, [globalContext.authInfo?.teamId]);

  useEffect(() => {
    loadConnection();
  }, [loadConnection]);

  const usesNces = strategy[0] === 'NCES_ID';
  const usesName = strategy[0] === 'NAME_ADDRESS';

  const canSubmit =
    strategy.length > 0 &&
    (!usesNces || !!hsNcesIdField) &&
    (!usesName || (!!hsCompanyNameField && !!hsStateField));

  const handleMatchAndPreview = async () => {
    setIsCreating(true);
    try {
      const response = await eduleadV4.matches.create({
        // One per submit: a retry of this click resumes the same run instead of starting another
        request_id: crypto.randomUUID(),
        body: {
          strategy: usesNces ? 'MATCH_STRATEGY_NCES_ID' : 'MATCH_STRATEGY_NAME_ADDRESS',
          hubspot_nces_id_property: usesNces ? hsNcesIdField : undefined,
          hubspot_name_property: usesName ? hsCompanyNameField : undefined,
          hubspot_state_property: usesName && hsStateField ? hsStateField : undefined,
          hubspot_address_property: usesName && hsAddressField ? hsAddressField : undefined,
        },
      });

      if (response.match?.id) {
        navigate(`/tools/hubspot-companies/${response.match.id}`);
      }
    } catch (error) {
      toastV4Error(error, 'Failed to start the match run');
    } finally {
      setIsCreating(false);
    }
  };

  if (isCheckingConnection) {
    return <div className="content content-1000"><LoadingBars numberOfRows={6} /></div>;
  }

  if (!isConnected) {
    return (
      <div className="content content-1000">
        <NothingHere text='Connect HubSpot to match your companies against our institutions.' />
        <Flex justifyContent='center' marginTop={15}>
          <Link to='/settings/integrations/hubspot'>
            <Button size='small' variation='primary' className='tiny'>Go to HubSpot integration</Button>
          </Link>
        </Flex>
      </div>
    );
  }

  return (
    <div className="content content-1000">
      <div className="match-run">
        <h3 className="match-run__title match-run__title--standalone">
          <Link to='/tools/hubspot-companies'>HubSpot Company Enrichment</Link>
          {' / '}
          New Run
        </h3>

        <div className="match-run__section">
          <h3 className="match-run__question">How should we match your companies?</h3>

          <SelectableBubbles
            options={STRATEGY_OPTIONS}
            selected={strategy}
            onChange={value => setStrategy(value.length ? [value[value.length - 1]] : [])}
            multiple={false}
          />
        </div>

        {strategy.length > 0 && (
          <div className="match-run__section">
            <h3 className="match-run__question">Which of your properties hold that data?</h3>
            <div className="desc-2 match-run__section-hint">
              We only read these. Nothing is written back to HubSpot.
            </div>
            {usesNces && (
              <HubspotPropertyPicker
                label='NCES ID'
                properties={companyProperties}
                value={hsNcesIdField}
                onChange={setHsNcesIdField}
              />
            )}
            {usesName && (
              <>
                <HubspotPropertyPicker
                  label='Company name'
                  properties={companyProperties}
                  value={hsCompanyNameField}
                  onChange={setHsCompanyNameField}
                />
                <HubspotPropertyPicker
                  label='State'
                  hint='Companies are matched against institutions in the same state.'
                  properties={companyProperties}
                  value={hsStateField}
                  onChange={setHsStateField}
                />
                <HubspotPropertyPicker
                  label='Street address'
                  hint='Confirms a name match and separates institutions that share a name.'
                  optional
                  properties={companyProperties}
                  value={hsAddressField}
                  onChange={setHsAddressField}
                />
              </>
            )}
          </div>
        )}

        <div className="match-run__actions">
          <Button
            size='small'
            variation='primary'
            isDisabled={isCreating || !canSubmit}
            isLoading={isCreating}
            loadingText='Starting…'
            onClick={handleMatchAndPreview}
          >
            Match and preview<TbArrowRight style={{ marginLeft: 8 }} />
          </Button>
        </div>
      </div>
    </div>
  );
}
